import { lazy, Suspense } from "react";
import type { RouteRecord } from "vite-react-ssg";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PhoneBarProvider } from "@/contexts/PhoneBarContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { StickyPhoneBar } from "@/components/StickyPhoneBar";
import { Outlet } from "react-router-dom";

// Create query client
const queryClient = new QueryClient();

// Root layout wrapper with all providers
function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <PhoneBarProvider>
            <Toaster />
            <Sonner />
            <Outlet />
            <StickyPhoneBar />
          </PhoneBarProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

// Lazy load admin components (not pre-rendered)
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const PhoneLeads = lazy(() => import("./pages/admin/PhoneLeads"));
const PhoneNumbers = lazy(() => import("./pages/admin/PhoneNumbers"));
const SMSConversations = lazy(() => import("./pages/admin/SMSConversations"));
const SMSPhoneNumbers = lazy(() => import("./pages/admin/SMSPhoneNumbers"));
const SMSPhoneConversations = lazy(() => import("./pages/admin/SMSPhoneConversations"));
const SMSConversation = lazy(() => import("./pages/admin/SMSConversation"));
const SMSSettings = lazy(() => import("./pages/admin/SMSSettings"));
const AllLeads = lazy(() => import("./pages/admin/AllLeads"));
const AdminProjects = lazy(() => import("./pages/AdminProjects"));
const AdminPageProjects = lazy(() => import("./pages/AdminPageProjects"));
const AdminServices = lazy(() => import("./pages/AdminServices"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const CityGroups = lazy(() => import("./pages/admin/CityGroups"));
const FeaturedContent = lazy(() => import("./pages/admin/FeaturedContent"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const Appointments = lazy(() => import("./pages/admin/Appointments"));
const PriceBook = lazy(() => import("./pages/admin/PriceBook"));
const Estimates = lazy(() => import("./pages/admin/Estimates"));
const EstimateBuilder = lazy(() => import("./pages/admin/EstimateBuilder"));
const SalesPresentationMode = lazy(() => import("./pages/admin/SalesPresentationMode"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const NewCustomer = lazy(() => import("./pages/admin/NewCustomer"));
const EditCustomer = lazy(() => import("./pages/admin/EditCustomer"));
const CustomerDetail = lazy(() => import("./pages/admin/CustomerDetail"));
const ShingleColors = lazy(() => import("./pages/admin/ShingleColors"));
const InstallationPhases = lazy(() => import("./pages/admin/InstallationPhases"));
const PhaseProducts = lazy(() => import("./pages/admin/PhaseProducts"));
const ProductCategories = lazy(() => import("./pages/admin/ProductCategories"));
const ProposalView = lazy(() => import("./pages/admin/ProposalView"));
const ProposalGallery = lazy(() => import("./pages/admin/ProposalGallery"));
const PublicProposalView = lazy(() => import("./pages/PublicProposalView"));
const ChangeRequests = lazy(() => import("./pages/admin/ChangeRequests"));
const TierConfiguration = lazy(() => import("./pages/admin/TierConfiguration"));
const Stories = lazy(() => import("./pages/admin/Stories"));

// Eager load public pages for SSG pre-rendering
import Index from "./pages/Index";
import CityLanding from "./pages/CityLanding";
import ServiceLanding from "./pages/ServiceLanding";
import WelcomeLanding from "./pages/WelcomeLanding";
import BookAppointment from "./pages/BookAppointment";
import AdsRoofingLanding from "./pages/AdsRoofingLanding";
import NotFound from "./pages/NotFound";

// Fallback component for lazy-loaded admin routes
const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Service slugs for pre-rendering
const serviceSlugs = [
  "roofing",
  "chimney-repair", 
  "gutter-repair",
  "emergency-roof-repair",
  "skylights-repair-replacement"
];

// City slugs for pre-rendering
const citySlugs = [
  "bay-shore",
  "east-hampton",
  "freeport",
  "garden-city",
  "great-neck",
  "hempstead",
  "huntington",
  "lawrence",
  "riverhead",
  "smithtown"
];

// Generate all service/city combinations for pre-rendering
export function getStaticPaths(): string[] {
  const paths: string[] = [
    "/",
    "/book-appointment",
    "/welcome",
    "/ads/roofing",
  ];

  // Add service pages without city
  for (const service of serviceSlugs) {
    paths.push(`/${service}`);
  }

  // Add service + city combinations
  for (const service of serviceSlugs) {
    for (const city of citySlugs) {
      paths.push(`/${service}/${city}`);
    }
  }

  // Add city landing pages
  for (const city of citySlugs) {
    paths.push(`/city/${city}`);
  }

  return paths;
}

// Admin routes wrapped in Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<AdminFallback />}>
    <Component />
  </Suspense>
);

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // Public routes - will be pre-rendered
      {
        index: true,
        element: <Index />,
      },
      {
        path: "book-appointment",
        element: <BookAppointment />,
      },
      {
        path: ":serviceSlug/:cityName",
        element: <ServiceLanding />,
      },
      {
        path: ":serviceSlug",
        element: <ServiceLanding />,
      },
      {
        path: "city/:cityName?",
        element: <CityLanding />,
      },
      {
        path: "welcome",
        element: <WelcomeLanding />,
      },
      {
        path: "ads/roofing",
        element: <AdsRoofingLanding />,
      },
      
      // Admin routes - client-side only, not pre-rendered
      {
        path: "admin/login",
        element: withSuspense(AdminLogin),
      },
      {
        path: "admin/sms-phone-numbers/:phoneNumberId/conversations/:conversationId",
        element: withSuspense(SMSConversation),
      },
      {
        path: "admin/estimates/presentation/:estimateId",
        element: withSuspense(SalesPresentationMode),
      },
      {
        path: "admin/estimates/:estimateId/proposal",
        element: withSuspense(ProposalView),
      },
      {
        path: "proposal/public/:estimateId/:accessToken",
        element: withSuspense(PublicProposalView),
      },
      {
        path: "admin",
        element: withSuspense(AdminLayout),
        children: [
          { index: true, element: withSuspense(Dashboard) },
          { path: "analytics", element: withSuspense(AdminAnalytics) },
          { path: "all-leads", element: withSuspense(AllLeads) },
          { path: "calls", element: withSuspense(PhoneLeads) },
          { path: "leads", element: withSuspense(AdminLeads) },
          { path: "phone-leads", element: withSuspense(PhoneLeads) },
          { path: "phone-numbers", element: withSuspense(PhoneNumbers) },
          { path: "sms-conversations", element: withSuspense(SMSConversations) },
          { path: "sms-phone-numbers", element: withSuspense(SMSPhoneNumbers) },
          { path: "sms-phone-numbers/:phoneNumberId/conversations", element: withSuspense(SMSPhoneConversations) },
          { path: "sms-settings", element: withSuspense(SMSSettings) },
          { path: "projects", element: withSuspense(AdminProjects) },
          { path: "categories", element: withSuspense(AdminCategories) },
          { path: "page-projects", element: withSuspense(AdminPageProjects) },
          { path: "services", element: withSuspense(AdminServices) },
          { path: "appointments", element: withSuspense(Appointments) },
          { path: "city-groups", element: withSuspense(CityGroups) },
          { path: "featured-content", element: withSuspense(FeaturedContent) },
          { path: "price-book", element: withSuspense(PriceBook) },
          { path: "estimates", element: withSuspense(Estimates) },
          { path: "estimates/builder/:id?", element: withSuspense(EstimateBuilder) },
          { path: "proposal-gallery", element: withSuspense(ProposalGallery) },
          { path: "customers", element: withSuspense(Customers) },
          { path: "customers/new", element: withSuspense(NewCustomer) },
          { path: "customers/:id/edit", element: withSuspense(EditCustomer) },
          { path: "customers/:id", element: withSuspense(CustomerDetail) },
          { path: "shingle-colors", element: withSuspense(ShingleColors) },
          { path: "installation-phases", element: withSuspense(InstallationPhases) },
          { path: "phase-products", element: withSuspense(PhaseProducts) },
          { path: "product-categories", element: withSuspense(ProductCategories) },
          { path: "tier-configuration", element: withSuspense(TierConfiguration) },
          { path: "change-requests", element: withSuspense(ChangeRequests) },
          { path: "stories", element: withSuspense(Stories) },
          { path: "settings", element: withSuspense(AdminSettings) },
        ],
      },
      
      // Catch-all route
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];
